import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, Boxes, RotateCcw, Ban, Trash2,
} from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Modal, Badge, Table, EmptyState, Tooltip } from '../ui-v2.5';
import { getIcon } from '../inventario/icons';
import {
  listRecursos, actualizarRecurso, desactivarRecurso,
  eliminarRecursoPermanente,
} from './api';
import EliminarModal from './components/EliminarModal';
import IncorporacionesTabs from './IncorporacionesTabs';

/**
 * Recursos — pantalla de administración (§3.6). Tabla (nombre, icono, inventariable, tipo de activo,
 * área responsable, por defecto, orden) + editor en Modal (crear/editar/reactivar/desactivar).
 *
 * Coherencia inventariable ↔ tipo de activo VALIDADA EN CLIENTE antes de enviar: el CHECK de la BBDD
 * nunca debe aflorar en la UI. Al desmarcar inventariable, el tipo se limpia y se deshabilita.
 * El desplegable de tipos se alimenta de /tipos-activo, AGRUPADO POR ÁMBITO con el ámbito visible en
 * la etiqueta (desambigua el 'movil' que existe en interno y en colaborador).
 *
 * DS: ui-v2.5 (Modal, Input, Select, Switch, Badge, Table, EmptyState) + Button/PageHeader de ui-v2 +
 * IconPicker reutilizado de Soporte. toast global; mismos estados carga/vacío/error y reactivación
 * que la pantalla de Áreas.
 */


const cuerpoError = (e) => (e && e.response && e.response.data) || {};

export default function Recursos() {
  const navigate = useNavigate();
  // El formulario vive en su propia página desde que creció con el tipo, los campos de
  // credencial y el editor rico. Las otras cuatro pantallas de configuración siguen con modal.
  const RUTA_NUEVO = '/gestionpersonas/personas/incorporaciones/recursos/nuevo';
  const rutaEditar = (r) => `/gestionpersonas/personas/incorporaciones/recursos/${r.id}`;
  const [recursos, setRecursos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [aDesactivar, setADesactivar] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      // `listAreas` ya no hace falta aquí: la tabla pinta `r.area`, que el servidor decora.
      // Las áreas las carga el formulario, que es quien tiene el desplegable.
      const recs = await listRecursos();
      setRecursos(recs);
    } catch (e) {
      console.error('[incorp][recursos] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);


  // Reactivar desde la tabla (recurso inactivo): PUT con el payload completo + activo=true.
  async function reactivar(r) {
    setAccionId(r.id);
    try {
      await actualizarRecurso(r.id, {
        nombre: r.nombre, icono: r.icono, inventariable: r.inventariable,
        inv_asset_type_id: r.inv_asset_type_id, inv_asset_type_colab_id: r.inv_asset_type_colab_id, area_id: r.area_id,
        por_defecto: r.por_defecto, orden: r.orden, activo: true,
      });
      toast.success(`Recurso "${r.nombre}" reactivado.`);
      await cargar();
    } catch (e) {
      console.error('[incorp][recursos] reactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el recurso.');
    } finally {
      setAccionId(null);
    }
  }

  const columns = [
    { key: 'icono', header: '', width: 48, render: (r) => <IconoCell name={r.icono} /> },
    {
      key: 'nombre', header: 'Nombre',
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${r.activo ? 'text-v25-ink-900' : 'text-v25-ink-400'}`}>{r.nombre}</span>
          {!r.activo && <Badge tone="expired">Desactivado</Badge>}
        </div>
      ),
    },
    {
      key: 'tipo_recurso', header: 'Tipo', width: 110,
      render: (r) => (r.tipo === 'credencial'
        ? <Badge tone="received">Credencial</Badge>
        : <Badge tone="info">Material</Badge>),
    },
    {
      key: 'inventariable', header: 'Inventariable', width: 120,
      render: (r) => (r.tipo === 'credencial'
        ? <span className="text-v25-ink-400">—</span>
        : (r.inventariable ? <Badge tone="active">Sí</Badge> : <Badge tone="info">No</Badge>)),
    },
    {
      key: 'tipo', header: 'Tipo de activo',
      // Un recurso, un tipo por ámbito (15/10). Si es inventariable y le falta uno, se dice en ámbar:
      // el material de ese ámbito no tendrá área en las bajas.
      render: (r) => (r.tipo === 'credencial' || !r.inventariable ? <span className="text-v25-ink-400">—</span> : (
        <span className="flex flex-col text-[12px] leading-tight">
          {[['Internos', r.tipo_activo], ['Colaboradoras', r.tipo_activo_colab]].map(([etq, ti]) => (
            <span key={etq} className={ti ? 'text-v25-ink-700' : 'text-amber-700'}>
              <span className="text-v25-ink-400">{etq}: </span>{ti ? ti.nombre : 'sin tipo'}
            </span>
          ))}
        </span>
      )),
    },
    {
      key: 'area', header: 'Área',
      render: (r) => (r.area ? (
        <span className="inline-flex items-center gap-1.5 text-v25-ink-700">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.area.color || '#94a3b8' }} />
          {r.area.nombre}
        </span>
      ) : <span className="text-v25-ink-400">—</span>),
    },
    { key: 'por_defecto', header: 'Por defecto', width: 110, render: (r) => (r.por_defecto ? 'Sí' : 'No') },
    { key: 'orden', header: 'Orden', width: 80, render: (r) => r.orden },
    {
      key: 'acciones', header: '', width: 190,
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(rutaEditar(r))}><Pencil />Editar</Button>
          {r.activo ? (
            <Tooltip content="Desactivar"><Button variant="ghost" size="icon" aria-label={`Desactivar ${r.nombre}`} onClick={() => setADesactivar(r)}><Ban /></Button></Tooltip>
          ) : (
            <Tooltip content="Reactivar">
              <Button variant="ghost" size="icon" aria-label={`Reactivar ${r.nombre}`} disabled={accionId === r.id} onClick={() => reactivar(r)}>
                {accionId === r.id ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              </Button>
            </Tooltip>
          )}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
          <Tooltip content="Eliminar permanentemente">
            <Button variant="ghost" size="icon" aria-label={`Eliminar ${r.nombre}`} onClick={() => setAEliminar(r)} className="text-red-600 hover:bg-red-50 hover:text-red-700">
              <Trash2 />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Recursos"
        subtitle="Catálogo de recursos que se entregan o preparan en una incorporación."
        action={<Button onClick={() => navigate(RUTA_NUEVO)}><Plus />Nuevo recurso</Button>}
      />

      <IncorporacionesTabs active="recursos" />

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando recursos…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar los recursos.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : recursos.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={Boxes}
          title="Aún no hay recursos"
          description="Crea el primer recurso del catálogo de incorporaciones."
          action={<Button onClick={() => navigate(RUTA_NUEVO)}><Plus />Nuevo recurso</Button>}
        />
      ) : (
        <Table columns={columns} rows={recursos} />
      )}

      {aDesactivar && (
        <DesactivarRecursoModal
          recurso={aDesactivar}
          onClose={() => setADesactivar(null)}
          onDesactivado={() => { setADesactivar(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar recurso"
          subtitulo={aEliminar.nombre}
          exitoMsg={`Recurso «${aEliminar.nombre}» eliminado permanentemente.`}
          eliminar={() => eliminarRecursoPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará el recurso <strong>«{aEliminar.nombre}»</strong> de forma permanente. Desaparece del
          catálogo y ya no podrá añadirse a nuevos perfiles. Esta acción no se puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// Celda de icono (cuadro con el icono lucide del catálogo).
function IconoCell({ name }) {
  const Icon = getIcon(name);
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-v25-md bg-v25-surface-sunken">
      <Icon className="h-4 w-4 text-v25-ink-700" />
    </span>
  );
}

// El editor de recurso ya NO es un modal: vive en RecursoForm.jsx, con sus dos rutas hijas.
// Los dos modales que quedan son CONFIRMACIONES, no formularios, y por eso siguen siendo modales.

// ─────────────────────────── Modal danger (desactivar) ───────────────────────────
function DesactivarRecursoModal({ recurso, onClose, onDesactivado }) {
  const [procesando, setProcesando] = useState(false);

  async function confirmar() {
    setProcesando(true);
    try {
      await desactivarRecurso(recurso.id);
      toast.success(`Recurso "${recurso.nombre}" desactivado.`);
      onDesactivado();
    } catch (e) {
      console.error('[incorp][recursos] desactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo desactivar el recurso.');
    } finally {
      setProcesando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      tone="danger"
      title="Desactivar recurso"
      subtitle={recurso.nombre}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmar} disabled={procesando}>
            {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Desactivar
          </Button>
        </>
      )}
    >
      <p className="text-sm text-v25-ink-700">
        El recurso dejará de ofrecerse en nuevas incorporaciones. Podrás reactivarlo después.
        ¿Seguro que quieres desactivar <strong>«{recurso.nombre}»</strong>?
      </p>
    </Modal>
  );
}
